import React from 'react';
import { fmtNum } from '../../utils/formatArcaNumbers';

interface NewPlanAdviceProps {
	/** Comisión de gestión del plan, de comparsa. */
	managementFeePct: number;
	/** Rentabilidad neta anualizada del plan, de comparsa. */
	annualPct: number;
	translate: (key: string) => string;
}

// Umbral de comisión de gestión que separa un plan indexado (comisión baja) de uno de
// gestión activa/autor (comisión mayor a cambio de buscar más rentabilidad).
const LOW_COST_MGMT_THRESHOLD = 1;

/**
 * Consejo de asesor para un plan nuevo (sin comparación con origen): resalta la virtud del
 * plan elegido en absoluto — comisiones mínimas si es indexado, o rentabilidad de autor si
 * es gestión activa (asumiendo algo más de comisión, pero a cuenta).
 */
const NewPlanAdvice: React.FC<NewPlanAdviceProps> = ({
	managementFeePct,
	annualPct,
	translate
}) => {
	const fmtPct = (n: number) => `${fmtNum(n, 2)}%`;

	const adviceCase = managementFeePct < LOW_COST_MGMT_THRESHOLD ? 'lowCost' : 'active';

	const highlight =
		translate(`individual.newPlanAdvice.${adviceCase}.highlight`) ||
		{
			lowCost: 'Comisiones de las más bajas del mercado.',
			active: 'Gestión de autor con rentabilidades de las más altas.'
		}[adviceCase];

	const body = (
		translate(`individual.newPlanAdvice.${adviceCase}.body`) ||
		{
			lowCost:
				'Este plan replica el mercado con una comisión mínima ({comm} anual), así que casi toda la rentabilidad se queda para ti en lugar de irse en comisiones. Una opción sencilla, eficiente y muy sólida a largo plazo.',
			active:
				'Es un plan de gestión activa: cobra algo más de comisión ({comm} anual), pero a cambio persigue —e históricamente ha logrado— rentabilidades muy por encima de la media ({return} anualizado). Si te compensa pagar por una gestión experta, sale a cuenta.'
		}[adviceCase]
	)
		.replace('{comm}', fmtPct(managementFeePct))
		.replace('{return}', fmtPct(annualPct));

	return (
		<div className="rounded-xl border-l-4 border-primary bg-primary/5 p-4">
			<p className="text-sm leading-relaxed text-foreground/80">
				<strong className="font-semibold text-foreground">{highlight}</strong> {body}
			</p>
		</div>
	);
};

export default NewPlanAdvice;
